<template>
  <div class="profile-page">
    <!-- Header Section -->
    <div class="profile-header mb-2">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h2 class="font-weight-bold text-primary mb-1">
            Profil Entreprise
          </h2>
          <p class="text-muted mb-0">
            Gérez les informations de votre entreprise et vos coordonnées.
          </p>
        </div>
        <div class="d-none d-md-block">
          <feather-icon
            icon="BriefcaseIcon"
            size="48"
            class="text-primary opacity-25"
          />
        </div>
      </div>
    </div>

    <b-card class="profile-card shadow-sm border-0 rounded-lg">
      <!-- form -->
      <validation-observer ref="simpleRules">
        <b-form class="mt-1">
          <b-row>
            <!-- Logo Section -->
            <b-col
              cols="12"
              class="mb-3"
            >
              <div class="d-flex align-items-center bg-light rounded p-2">
                <div class="mr-2">
                  <b-img
                    ref="refPreviewEl"
                    :src="company.logo"
                    height="80"
                    width="80"
                    class="rounded-circle shadow-sm object-cover"
                    style="object-fit: cover;"
                  />
                </div>
                <div class="flex-grow-1">
                  <h5 class="mb-1 font-weight-bold">
                    Logo de l'entreprise
                  </h5>
                  <small class="text-muted d-block mb-1">Format recommandé: PNG ou JPG. Max 2MB.</small>
                  <div class="d-inline-block">
                    <validation-provider
                      #default="{ errors }"
                      name="logo"
                      rules="required"
                    >
                      <b-form-file
                        ref="refInputEl"
                        v-model="company.logo"
                        placeholder="Choisir un fichier..."
                        :state="errors.length > 0 ? false:null"
                        accept="image/*"
                        @input="inputImageRenderer"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </div>
                </div>
              </div>
            </b-col>

            <!-- Company Details -->
            <b-col md="6">
              <b-form-group
                label="Nom entreprise"
                label-for="company-name"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="name"
                  rules="required"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="BriefcaseIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="company-name"
                      v-model="company.name"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Nom de votre société"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Matricule fiscale"
                label-for="tax-id"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="tax_identification_number"
                  rules="required"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="FileTextIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="tax-id"
                      v-model="company.tax_identification_number"
                      :state="errors.length > 0 ? false:null"
                      placeholder="MF123456789"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group
                label="Adresse complète"
                label-for="address"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="address"
                  rules="required"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="MapPinIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="address"
                      v-model="company.address"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Adresse du siège social"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Téléphone principal"
                label-for="phone1"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="phone1"
                  rules="required|integer|length:8"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="PhoneIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="phone1"
                      v-model="company.phone1"
                      :state="errors.length > 0 ? false:null"
                      maxlength="8"
                      type="number"
                      placeholder="12345678"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Téléphone secondaire (Optionnel)"
                label-for="phone2"
                class="mb-2"
              >
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="PhoneIcon" />
                  </b-input-group-prepend>
                  <b-form-input
                    id="phone2"
                    v-model="company.phone2"
                    placeholder="12345678"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              class="mt-2 d-flex justify-content-end"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="px-4"
                @click="save"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                />
                Enregistrer les modifications
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BForm, BRow, BCol, BFormGroup, BFormInput, BImg, BFormFile, BButton, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'
import { ref } from '@vue/composition-api'
import storeAuth from '@/store/store'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'

export default {
  components: {
    BCard,
    BForm,
    BImg,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormFile,
    BInputGroup,
    BInputGroupPrepend,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      company: {
        name: '',
        address: '',
        phone1: '',
        phone2: '',
        logo: '',
        tax_identification_number: '',
      },
      required,
      blogFile: null,
      categoryOption: ['Fashion', 'Food', 'Gaming', 'Quote', 'Video'],
      statusOption: ['Published', 'Pending', 'Draft'],
      snowOption: {
        theme: 'snow',
      },
    }
  },
  created() {
    if (storeAuth.state.company != null) {
      this.company = storeAuth.state.company
    }
  },
  methods: {
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    async save() {
      const isValid = await this.$refs.simpleRules.validate()
      if (!isValid) return

      const {
        name, address, phone1, phone2, tax_identification_number, logo,
      } = this.company
      const formData = new FormData()
      formData.append('name', name)
      formData.append('address', address)
      formData.append('phone1', phone1)
      formData.append('phone2', phone2)
      formData.append('tax_identification_number', tax_identification_number)
      formData.append('logo', logo)

      if (storeAuth.state.company != null) {
        try {
          const { data } = await axios.put(`/api/companies/update/${1}/`, formData)
          setTimeout(() => {
            this.showToast('success', 'top-center', 'Votre données modifiées avec succes')
          }, 1000)
          storeAuth.commit('setCompany', data)
          this.$router.push({ name: 'dashboard' })
        } catch (error) {
          setTimeout(() => {
            this.showToast('danger', 'top-center', 'errror')
          }, 1000)
        }
      } else {
        try {
          const { data } = await axios.post('/api/companies/create/', formData)
          setTimeout(() => {
            this.showToast('success', 'top-center', 'Votre données ajoutées avec succes')
          }, 1000)
          storeAuth.commit('setCompany', data)
          this.$router.push({ name: 'dashboard' })
        } catch (error) {
          setTimeout(() => {
            this.showToast('danger', 'top-center', 'errror')
          }, 1000)
        }
      }
    },
  },

  setup() {
    const refInputEl = ref(null)
    const refPreviewEl = ref(null)

    const { inputImageRenderer } = useInputImageRenderer(refInputEl, base64 => { refPreviewEl.value.src = base64 })

    return {
      refInputEl,
      refPreviewEl,
      inputImageRenderer,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/pages/page-blog.scss';
</style>
